import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { ChevronLeft, Cpu, Library, Server } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import mcpServerService from '../../services/mcpServerService';
import agentService from '../../services/agentService';
import folderService from '../../services/folderService';
import folderD3Service from '../../services/folderD3Service';
import {
  CREDENTIAL_MODES,
  CREDENTIAL_MODE_OPTIONS,
  OAUTH_CREDENTIAL_MODE,
  OAUTH_INBOUND_MODE,
  PER_USER_AUTH_DISABLED_HINT,
  PER_USER_AUTH_TYPES,
  SERVER_TYPES,
  UPSTREAM_AUTH_TYPES,
  allowsUrlKey,
  buildOauthConfigBody,
  describeError,
  emptyOauthForm,
  getSecretLabel,
  isAuthTypeAllowedPerUser,
  isOauthMode,
  storesNoUpstreamKey,
  toBaseCredentialMode,
  unwrapList,
  validateOauthForm,
  withUrlKey,
} from './mcpUtils';
import McpOAuthFields from './McpOAuthFields';
import McpExposeErrorsField from './McpExposeErrorsField';
import McpInstructionsField from './McpInstructionsField';
import styles from './CreateMcpServerDialog.module.scss';

const TYPE_CARDS = [
  { ...SERVER_TYPES.api, Icon: Server },
  { ...SERVER_TYPES.agent, Icon: Cpu },
  { ...SERVER_TYPES.knowledge, Icon: Library },
];

/** Agents load a page at a time as the dropdown is scrolled. */
const AGENTS_PAGE_SIZE = 20;

const emptyForm = {
  name: '',
  description: '',
  instructions: '',
  inbound_auth_mode: 'token',
  rate_limit_per_min: 60,
  upstream_base_url: '',
  upstream_auth_type: 'BEARER_TOKEN',
  upstream_api_key: '',
  credential_mode: 'shared',
  expose_upstream_errors: false,
  oauth: emptyOauthForm(),
  diy_agent_id: '',
  folder_ids: [],
  is_dim3: false,
};

/**
 * Create an MCP server (§2).
 *
 * The three server types are three separate starting points rather than one
 * dropdown, because everything after this differs: `api` goes on to import a
 * spec, while `agent` and `knowledge` have their tools created for them and go
 * straight to curation.
 */
const CreateMcpServerDialog = ({ open, onClose, onCreated, onError }) => {
  const { isDarkMode } = useTheme();
  const [step, setStep] = useState(0);
  const [serverType, setServerType] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingMoreAgents, setLoadingMoreAgents] = useState(false);
  const [agentsPage, setAgentsPage] = useState(1);
  const [agentsHasMore, setAgentsHasMore] = useState(false);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: { main: '#e36250' },
          secondary: { main: '#8b5cf6' },
        },
      }),
    [isDarkMode]
  );

  useEffect(() => {
    if (open) {
      setStep(0);
      setServerType(null);
      setForm(emptyForm);
      setErrors({});
      setSubmitError(null);
      setSubmitting(false);
    }
  }, [open]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  /**
   * One page at a time, appended as the dropdown is scrolled.
   *
   * This used to ask for every agent at once with `size=200` — which the API
   * rejects (it caps `size` at 100), so the picker silently came up empty. A
   * small page also means the dropdown opens immediately for someone with
   * hundreds of agents, instead of waiting on several round trips.
   */
  const loadAgents = useCallback(async (pageNum = 1) => {
    if (pageNum === 1) setLoadingAgents(true);
    else setLoadingMoreAgents(true);
    try {
      const data = await agentService.getAgents(pageNum, AGENTS_PAGE_SIZE);
      const batch = unwrapList(data, 'agents', 'diy_agents');
      setAgents((prev) => (pageNum === 1 ? batch : [...prev, ...batch]));
      setAgentsPage(pageNum);
      // `total_pages` is authoritative when present; a short page is the
      // fallback signal for an API that doesn't send it.
      const totalPages = Number(data?.total_pages) || 0;
      setAgentsHasMore(
        totalPages > 0 ? pageNum < totalPages : batch.length === AGENTS_PAGE_SIZE
      );
    } catch (e) {
      if (pageNum === 1) setAgents([]);
      setAgentsHasMore(false);
    } finally {
      setLoadingAgents(false);
      setLoadingMoreAgents(false);
    }
  }, []);

  /** Fetch the next page once the list is within a row or two of the bottom. */
  const handleAgentListScroll = (event) => {
    if (!agentsHasMore || loadingAgents || loadingMoreAgents) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 80) {
      loadAgents(agentsPage + 1);
    }
  };

  const loadFolders = useCallback(async (isDim3) => {
    setLoadingFolders(true);
    try {
      const data = isDim3 ? await folderD3Service.getFolders() : await folderService.getFolders();
      setFolders(unwrapList(data, 'folders'));
    } catch (e) {
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  // Only fetch the picker data the chosen type actually needs.
  useEffect(() => {
    if (step !== 1) return;
    if (serverType === 'agent') loadAgents();
    if (serverType === 'knowledge') loadFolders(form.is_dim3);
  }, [step, serverType, form.is_dim3, loadAgents, loadFolders]);

  const chooseType = (type) => {
    setServerType(type);
    setStep(1);
    // Credential mode is only sent for API servers, so an OAuth choice carried
    // over from a previous back-and-forth would leave `inbound_auth_mode:
    // 'oauth'` with nothing to match it — exactly the mismatch publish refuses.
    if (type !== 'api') {
      setForm((f) =>
        isOauthMode(f.credential_mode)
          ? { ...f, credential_mode: 'shared', inbound_auth_mode: 'token' }
          : f
      );
    }
  };

  const baseMode = toBaseCredentialMode(form.credential_mode);
  const perUser = baseMode === 'per_user';
  const oauth = isOauthMode(baseMode);
  // Both per-user and OAuth leave the owner's key out of it entirely.
  const noStoredKey = storesNoUpstreamKey(form.credential_mode);
  const urlKey = allowsUrlKey(form.credential_mode);

  /**
   * Switching off the shared key drops any typed key (it would never be sent)
   * and moves off an auth type that can't carry a single supplied secret.
   * OAuth additionally owns `inbound_auth_mode`: the two fields have to agree
   * or publish is refused, so they always move together.
   */
  const handleCredentialModeChange = (mode) => {
    setForm((f) => {
      const next = { ...f };
      if (isOauthMode(mode)) {
        next.credential_mode = OAUTH_CREDENTIAL_MODE;
        next.inbound_auth_mode = OAUTH_INBOUND_MODE;
      } else {
        // Choosing per-user from the dropdown always lands on the header
        // variant; the checkbox below opts into the URL one.
        next.credential_mode = mode === 'per_user' ? withUrlKey(false) : 'shared';
        // Leaving OAuth leaves behind an inbound mode nothing else can use.
        if (f.inbound_auth_mode === OAUTH_INBOUND_MODE) next.inbound_auth_mode = 'token';
      }
      // No mode but shared keeps a key of the owner's.
      if (mode !== 'shared') next.upstream_api_key = '';
      // The single-secret restriction is a per-user-key rule. OAuth forwards the
      // verified token as `Authorization: Bearer` whatever this says, so its
      // value is ignored rather than constrained.
      if (mode === 'per_user' && !isAuthTypeAllowedPerUser(f.upstream_auth_type)) {
        next.upstream_auth_type = PER_USER_AUTH_TYPES[1]; // BEARER_TOKEN
      }
      return next;
    });
    setErrors((e) => ({ ...e, upstream_auth_type: undefined, oauth: undefined }));
  };

  const handleUrlKeyChange = (allow) =>
    setForm((f) => ({ ...f, credential_mode: withUrlKey(allow) }));

  const setOauthField = (key, value) => {
    setForm((f) => ({ ...f, oauth: { ...f.oauth, [key]: value } }));
    setErrors((e) => (e.oauth?.[key] ? { ...e, oauth: { ...e.oauth, [key]: undefined } } : e));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Give the server a name.';
    if (serverType === 'agent' && !form.diy_agent_id) next.diy_agent_id = 'Choose an agent.';
    if (serverType === 'knowledge' && form.folder_ids.length === 0) {
      next.folder_ids = 'Choose at least one folder.';
    }
    if (form.upstream_base_url && !/^https?:\/\//i.test(form.upstream_base_url.trim())) {
      next.upstream_base_url = 'Must start with http:// or https://';
    }
    // Per-user keys only — OAuth ignores the upstream auth type entirely, and
    // its dropdown isn't even rendered.
    if (serverType === 'api' && perUser && !isAuthTypeAllowedPerUser(form.upstream_auth_type)) {
      next.upstream_auth_type = PER_USER_AUTH_DISABLED_HINT;
    }
    if (serverType === 'api' && oauth) {
      const oauthErrors = validateOauthForm(form.oauth);
      if (Object.keys(oauthErrors).length) next.oauth = oauthErrors;
    }
    const rate = Number(form.rate_limit_per_min);
    if (!Number.isFinite(rate) || rate < 1) next.rate_limit_per_min = 'Must be at least 1.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError(null);

    const body = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      instructions: form.instructions.trim() || undefined,
      server_type: serverType,
      inbound_auth_mode: form.inbound_auth_mode,
      rate_limit_per_min: Number(form.rate_limit_per_min),
    };

    if (serverType === 'api') {
      if (form.upstream_base_url.trim()) body.upstream_base_url = form.upstream_base_url.trim();
      body.upstream_auth_type = form.upstream_auth_type;
      body.credential_mode = form.credential_mode;
      body.expose_upstream_errors = form.expose_upstream_errors;
      // Per-user and sign-in servers store no key at all.
      if (!noStoredKey && form.upstream_api_key.trim() && form.upstream_auth_type !== 'NO_AUTH') {
        body.upstream_api_key = form.upstream_api_key.trim();
      }
      if (oauth) body.oauth_config = buildOauthConfigBody(form.oauth);
    } else if (serverType === 'agent') {
      body.diy_agent_id = form.diy_agent_id;
    } else if (serverType === 'knowledge') {
      body.folder_ids = form.folder_ids;
      body.is_dim3 = form.is_dim3;
    }

    try {
      const created = await mcpServerService.createServer(body);
      onCreated?.(created);
    } catch (e) {
      const message = describeError(e, "Couldn't create the server.");
      setSubmitError(message);
      onError?.(message);
    } finally {
      setSubmitting(false);
    }
  };

  const anonymous = form.inbound_auth_mode === 'none';
  // Drives the access row off the field that row edits: its dropdown has no
  // `oauth` option, so rendering it on an OAuth value would show a blank one.
  const oauthInbound = form.inbound_auth_mode === OAUTH_INBOUND_MODE;

  const renderTypePicker = () => (
    <div className={styles.typeGrid}>
      {TYPE_CARDS.map(({ value, label, description, Icon }) => (
        <button key={value} type="button" className={styles.typeCard} onClick={() => chooseType(value)}>
          <span className={styles.typeIcon}>
            <Icon size={22} />
          </span>
          <span className={styles.typeLabel}>{label}</span>
          <span className={styles.typeDesc}>{description}</span>
        </button>
      ))}
    </div>
  );

  const renderForm = () => (
    <Box className={styles.form}>
      <TextField
        label="Name"
        value={form.name}
        onChange={(e) => setField('name', e.target.value)}
        error={!!errors.name}
        helperText={errors.name}
        fullWidth
        size="small"
        autoFocus
      />
      <TextField
        label="Description"
        value={form.description}
        onChange={(e) => setField('description', e.target.value)}
        fullWidth
        size="small"
        multiline
        minRows={2}
        placeholder="What this connector is for — models see this too."
      />
      <McpInstructionsField
        value={form.instructions}
        onChange={(v) => setField('instructions', v)}
      />

      {serverType === 'api' && (
        <>
          <TextField
            label="Upstream base URL (optional)"
            value={form.upstream_base_url}
            onChange={(e) => setField('upstream_base_url', e.target.value)}
            error={!!errors.upstream_base_url}
            helperText={errors.upstream_base_url || 'Leave blank — importing a spec usually fills this in.'}
            fullWidth
            size="small"
            placeholder="https://api.acme.com/v1"
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Who provides the API key?</InputLabel>
            <Select
              label="Who provides the API key?"
              value={baseMode}
              onChange={(e) => handleCredentialModeChange(e.target.value)}
            >
              {CREDENTIAL_MODE_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" className={styles.help}>
              {CREDENTIAL_MODES[baseMode].helper}
            </Typography>
          </FormControl>

          {perUser && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={urlKey}
                    onChange={(e) => handleUrlKeyChange(e.target.checked)}
                    size="small"
                  />
                }
                label="Allow the key in the URL"
              />
              <Typography variant="caption" className={styles.help}>
                For apps that can’t send custom headers — including Claude’s website connector
                screen. The key becomes part of the link, so treat it as replaceable.
              </Typography>

              {/* The likely mistake on this screen: URL mode is enabled to reach
                  Claude web, but the access token can't travel in a URL either,
                  so it still won't connect. */}
              {urlKey && form.inbound_auth_mode === 'token' && (
                <Alert severity="warning">
                  The access token can’t go in the URL, so Claude’s website still won’t be able to
                  connect. Set <strong>“Who can call this server”</strong> to{' '}
                  <strong>“Anyone with the URL”</strong>.
                </Alert>
              )}
            </>
          )}

          {/* No explainer above the block — the credential-mode helper directly
              above already says nobody pastes a key. */}
          {oauth && (
            <McpOAuthFields
              value={form.oauth}
              errors={errors.oauth || {}}
              onChange={setOauthField}
            />
          )}

          {/* Hidden entirely in OAuth mode: the verified token is forwarded as
              `Authorization: Bearer` regardless of what this says, so offering
              the choice would be offering a control that does nothing. */}
          {!oauth && (
            <Box className={styles.row}>
              <FormControl size="small" fullWidth error={!!errors.upstream_auth_type}>
                <InputLabel>Upstream auth</InputLabel>
                <Select
                  label="Upstream auth"
                  value={form.upstream_auth_type}
                  onChange={(e) => setField('upstream_auth_type', e.target.value)}
                >
                  {UPSTREAM_AUTH_TYPES.map((a) => {
                    const blocked = perUser && !isAuthTypeAllowedPerUser(a.value);
                    return (
                      <MenuItem key={a.value} value={a.value} disabled={blocked}>
                        {a.label}
                        {blocked && (
                          <Tooltip title={PER_USER_AUTH_DISABLED_HINT}>
                            <span className={styles.menuHint}>not available</span>
                          </Tooltip>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.upstream_auth_type && (
                  <Typography variant="caption" className={styles.helpError}>
                    {errors.upstream_auth_type}
                  </Typography>
                )}
              </FormControl>

              {/* Per-user stores no key — leaving this visible would create a
                  dead credential that publish then warns about. */}
              {!noStoredKey && form.upstream_auth_type !== 'NO_AUTH' && (
                <TextField
                  label={getSecretLabel(form.upstream_auth_type)}
                  value={form.upstream_api_key}
                  onChange={(e) => setField('upstream_api_key', e.target.value)}
                  type="password"
                  fullWidth
                  size="small"
                  placeholder="sk-live-…"
                />
              )}
            </Box>
          )}

          {perUser && (
            <Alert severity="info">
              No key is stored on this server. Each person adds their own in their Claude / Cursor
              settings, and only ever reaches their own data.
            </Alert>
          )}

          <McpExposeErrorsField
            checked={form.expose_upstream_errors}
            onChange={(v) => setField('expose_upstream_errors', v)}
            credentialMode={form.credential_mode}
          />
        </>
      )}

      {serverType === 'agent' && (
        <FormControl size="small" fullWidth error={!!errors.diy_agent_id}>
          <InputLabel>Agent</InputLabel>
          <Select
            label="Agent"
            value={form.diy_agent_id}
            onChange={(e) => setField('diy_agent_id', e.target.value)}
            MenuProps={{
              // The scroll listener goes on the menu's own scroll container —
              // the Select itself never scrolls.
              PaperProps: { onScroll: handleAgentListScroll, sx: { maxHeight: 320 } },
            }}
          >
            {loadingAgents && (
              <MenuItem disabled value="">
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading agents…
              </MenuItem>
            )}
            {!loadingAgents && agents.length === 0 && (
              <MenuItem disabled value="">
                No agents found
              </MenuItem>
            )}
            {agents.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name || `Agent ${a.id}`}
              </MenuItem>
            ))}
            {loadingMoreAgents && (
              <MenuItem disabled value="">
                <CircularProgress size={14} sx={{ mr: 1 }} /> Loading more…
              </MenuItem>
            )}
            {/* Without this the list just stops, and there is no way to tell a
                short list from one that failed to page. */}
            {!loadingMoreAgents && !agentsHasMore && agents.length > AGENTS_PAGE_SIZE && (
              <MenuItem disabled value="">
                <Typography variant="caption">All {agents.length} agents loaded</Typography>
              </MenuItem>
            )}
          </Select>
          <Typography variant="caption" className={styles.help}>
            {errors.diy_agent_id || 'The agent is exposed as a single tool. Its tool is created for you.'}
          </Typography>
        </FormControl>
      )}

      {serverType === 'knowledge' && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={form.is_dim3}
                onChange={(e) => {
                  setField('is_dim3', e.target.checked);
                  setField('folder_ids', []);
                }}
              />
            }
            label="Use customer data (D3) folders"
          />
          <FormControl size="small" fullWidth error={!!errors.folder_ids}>
            <InputLabel>Folders</InputLabel>
            <Select
              multiple
              label="Folders"
              value={form.folder_ids}
              onChange={(e) => setField('folder_ids', e.target.value)}
              input={<OutlinedInput label="Folders" />}
              renderValue={(selected) =>
                folders
                  .filter((f) => selected.includes(f.id))
                  .map((f) => f.name || f.folder_name)
                  .join(', ')
              }
            >
              {loadingFolders && (
                <MenuItem disabled value="">
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading folders…
                </MenuItem>
              )}
              {!loadingFolders && folders.length === 0 && (
                <MenuItem disabled value="">
                  No folders found
                </MenuItem>
              )}
              {folders.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  <Checkbox checked={form.folder_ids.includes(f.id)} size="small" />
                  <ListItemText primary={f.name || f.folder_name || `Folder ${f.id}`} />
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" className={styles.help}>
              {errors.folder_ids || 'Exposed as two tools: search and fetch. Both are created for you.'}
            </Typography>
          </FormControl>
        </>
      )}

      <Box className={styles.row}>
        {/* OAuth replaces this setting outright — `inbound_auth_mode: 'oauth'`
            is sent with the credential mode, so there is nothing to ask and
            nothing to leave sitting beside the rate limit. */}
        {!oauthInbound && (
          <FormControl size="small" fullWidth>
            <InputLabel>Who can call this server</InputLabel>
            <Select
              label="Who can call this server"
              value={form.inbound_auth_mode}
              onChange={(e) => setField('inbound_auth_mode', e.target.value)}
            >
              <MenuItem value="token">Requires a token (recommended)</MenuItem>
              <MenuItem value="none">Anyone with the URL</MenuItem>
            </Select>
          </FormControl>
        )}
        <TextField
          label="Rate limit (calls/min)"
          value={form.rate_limit_per_min}
          onChange={(e) => setField('rate_limit_per_min', e.target.value)}
          error={!!errors.rate_limit_per_min}
          helperText={errors.rate_limit_per_min}
          type="number"
          size="small"
          fullWidth
          // Left on its own once the inbound field is gone; a number input
          // stretched across the dialog reads as though something is missing.
          sx={oauthInbound ? { maxWidth: 260 } : undefined}
        />
      </Box>

      {/* An anonymous server's URL is the only thing between the customer's
          upstream credentials and the internet, and it will be crawled. */}
      {anonymous && (
        <Alert severity="warning">
          This URL will be the only thing protecting your upstream credentials, and it{' '}
          <strong>will</strong> be crawled. Only use this for public, read-only data — publishing is
          blocked outright if any write tool is enabled.
        </Alert>
      )}

      {submitError && <Alert severity="error">{submitError}</Alert>}
    </Box>
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={submitting ? undefined : onClose}
        maxWidth={false}
        fullWidth
        scroll="paper"
        className={styles.dialog}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(24, 24, 27, 0.4)', backdropFilter: 'blur(2px)' } },
        }}
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle className={styles.dialogTitle} component="div">
          <Box className={styles.titleRow}>
            {step === 1 && (
              <IconButton
                size="small"
                onClick={() => setStep(0)}
                className={styles.headerHelpIcon}
                aria-label="Back to type"
                disabled={submitting}
              >
                <ChevronLeft size={18} />
              </IconButton>
            )}
            <Typography component="h3" variant="h6" className={styles.title}>
              {step === 0 ? 'New MCP server' : SERVER_TYPES[serverType]?.label}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" className={styles.closeButton} aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          {step === 0 ? (
            <>
              <Typography variant="body2" className={styles.lead}>
                What are you turning into a connector?
              </Typography>
              {renderTypePicker()}
            </>
          ) : (
            renderForm()
          )}
        </DialogContent>

        {step === 1 && (
          <DialogActions className={styles.dialogActions}>
            <Button onClick={onClose} variant="text" className={styles.cancelButton} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              className={styles.saveButton}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={15} color="inherit" /> : null}
            >
              {submitting ? 'Creating…' : 'Create server'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </ThemeProvider>
  );
};

export default CreateMcpServerDialog;
